import {defineMessages, FormattedMessage} from 'react-intl';

import {FEATURES} from '../../constants/features';
import {
  ARABIC,
  BULGARIAN,
  CATALAN,
  CHINESE_SIMPLIFIED,
  CHINESE_TRADITIONAL,
  CZECH,
  DANISH,
  DUTCH,
  ENGLISH,
  ENGLISH_UK,
  FINNISH,
  FRENCH,
  FRENCH_CA,
  GERMAN,
  GREEK,
  HEBREW,
  HINDI,
  HUNGARIAN,
  ITALIAN,
  JAPANESE,
  KOREAN,
  MALAY,
  NORWEGIAN,
  POLISH,
  PORTUGUESE,
  PORTUGUESE_BR,
  ROMANIAN,
  RUSSIAN,
  SLOVAK,
  SPANISH,
  SPANISH_MX,
  SWEDISH,
  THAI,
  TURKISH,
  UKRAINIAN,
  VIETNAMESE,
} from '../../constants/permissions';
import {featureToggle} from '../featureToggle/featureToggle';

export const LOCALES = {
  DE: 'de',
  EN: 'en',
  ES: 'es',
  FR: 'fr',
  HE: 'he',
  HI: 'hi',
  IT: 'it',
  JA: 'ja',
  NL: 'nl',
  PL: 'pl',
  PT: 'pt',
  RU: 'ru',
  TR: 'tr',
  ZH_HANS: 'zh-hans',
  ZH_HANT: 'zh-hant',
  BG: 'bg',
  ...(featureToggle(FEATURES.MAPI_326) ? {KO: 'ko'} : {}),
  ...(featureToggle(FEATURES.MAPI_327) ? {FR_CA: 'fr-ca'} : {}),
  ...(featureToggle(FEATURES.MAPI_328) ? {AR: 'ar'} : {}),
  ...(featureToggle(FEATURES.MAPI_329) ? {PT_BR: 'pt-br'} : {}),
  ...(featureToggle(FEATURES.MAPI_331) ? {EL: 'el'} : {}),
  ...(featureToggle(FEATURES.MAPI_332) ? {VI: 'vi'} : {}),
  ...(featureToggle(FEATURES.MAPI_333) ? {CS: 'cs'} : {}),
  ...(featureToggle(FEATURES.MAPI_334) ? {HU: 'hu'} : {}),
  ...(featureToggle(FEATURES.MAPI_335) ? {MS: 'ms'} : {}),
  ...(featureToggle(FEATURES.MAPI_336) ? {RO: 'ro'} : {}),
  ...(featureToggle(FEATURES.MAPI_337) ? {SK: 'sk'} : {}),
  ...(featureToggle(FEATURES.MAPI_338) ? {TH: 'th'} : {}),
  ...(featureToggle(FEATURES.MAPI_339) ? {UK: 'uk'} : {}),
  ...(featureToggle(FEATURES.MAPI_340) ? {EN_GB: 'en-gb'} : {}),
  ...(featureToggle(FEATURES.MAPI_341) ? {SV: 'sv'} : {}),
  ...(featureToggle(FEATURES.MAPI_342) ? {FI: 'fi'} : {}),
  ...(featureToggle(FEATURES.MAPI_343) ? {DA: 'da'} : {}),
  ...(featureToggle(FEATURES.MAPI_344) ? {NO: 'no'} : {}),
  ...(featureToggle(FEATURES.MAPI_345) ? {CA: 'ca'} : {}),
  ...(featureToggle(FEATURES.MAPI_346) ? {ES_MX: 'es-mx'} : {}),
};

export const RTL_LOCALES = [LOCALES.HE, LOCALES.AR];

export const localeOptions = [
  {
    label: 'Deutsch',
    multiLingualLabel: 'Deutsch - German',
    permission: GERMAN,
    value: LOCALES.DE,
  },
  {
    label: 'English',
    multiLingualLabel: 'English - default',
    permission: ENGLISH,
    value: LOCALES.EN,
  },
  {
    label: 'Español',
    multiLingualLabel: 'Español - Spanish',
    permission: SPANISH,
    value: LOCALES.ES,
  },
  {
    label: 'Français',
    multiLingualLabel: 'Français - French',
    permission: FRENCH,
    value: LOCALES.FR,
  },
  {
    label: 'עִברִית',
    multiLingualLabel: 'עִברִית - Hebrew',
    permission: HEBREW,
    value: LOCALES.HE,
  },
  {
    label: 'हिंदी',
    multiLingualLabel: 'हिंदी - Hindi',
    permission: HINDI,
    value: LOCALES.HI,
  },
  {
    label: 'Italiano',
    multiLingualLabel: 'Italiano - Italian',
    permission: ITALIAN,
    value: LOCALES.IT,
  },
  {
    label: '日本語',
    multiLingualLabel: '日本語 - Japanese',
    permission: JAPANESE,
    value: LOCALES.JA,
  },
  {
    label: 'Nederlands',
    multiLingualLabel: 'Nederlands - Dutch',
    permission: DUTCH,
    value: LOCALES.NL,
  },
  {
    label: 'Polski',
    multiLingualLabel: 'Polski - Polish',
    permission: POLISH,
    value: LOCALES.PL,
  },
  {
    label: 'Português',
    multiLingualLabel: 'Português - Portuguese',
    permission: PORTUGUESE,
    value: LOCALES.PT,
  },
  {
    label: 'Русский',
    multiLingualLabel: 'Русский - Russian',
    permission: RUSSIAN,
    value: LOCALES.RU,
  },
  {
    label: 'Türkçe',
    multiLingualLabel: 'Türkçe - Turkish',
    permission: TURKISH,
    value: LOCALES.TR,
  },
  {
    label: '简体中文',
    multiLingualLabel: '简体中文 - Chinese Simplified',
    permission: CHINESE_SIMPLIFIED,
    value: LOCALES.ZH_HANS,
  },
  {
    label: '繁體中文',
    multiLingualLabel: '繁體中文 - Chinese Traditional',
    permission: CHINESE_TRADITIONAL,
    value: LOCALES.ZH_HANT,
  },
  {
    label: 'Български език',
    multiLingualLabel: 'Български език - Bulgarian',
    permission: BULGARIAN,
    value: LOCALES.BG,
  },
  ...(featureToggle(FEATURES.MAPI_326)
    ? [
        {
          label: '한국어',
          multiLingualLabel: '한국어 - Korean',
          permission: KOREAN,
          value: LOCALES.KO,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_327)
    ? [
        {
          label: 'Français (Canada)',
          multiLingualLabel: 'Français (Canada) - French Canada',
          permission: FRENCH_CA,
          value: LOCALES.FR_CA,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_328)
    ? [
        {
          label: 'اَلْعَرَبِيَّةُ',
          multiLingualLabel: 'اَلْعَرَبِيَّةُ - Arabic',
          permission: ARABIC,
          value: LOCALES.AR,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_329)
    ? [
        {
          label: 'Português (Brasil)',
          multiLingualLabel: 'Português (Brasil) - Portuguese Brazil',
          permission: PORTUGUESE_BR,
          value: LOCALES.PT_BR,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_331)
    ? [
        {
          label: 'Νέα Ελληνικά',
          multiLingualLabel: 'Νέα Ελληνικά - Greek',
          permission: GREEK,
          value: LOCALES.EL,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_332)
    ? [
        {
          label: 'tiếng Việt',
          multiLingualLabel: 'tiếng Việt - Vietnamese',
          permission: VIETNAMESE,
          value: LOCALES.VI,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_333)
    ? [
        {
          label: 'Čeština',
          multiLingualLabel: 'Čeština - Czech',
          permission: CZECH,
          value: LOCALES.CS,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_334)
    ? [
        {
          label: 'Magyar nyelv',
          multiLingualLabel: 'Magyar nyelv - Hungarian',
          permission: HUNGARIAN,
          value: LOCALES.HU,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_335)
    ? [
        {
          label: 'Bahasa Melayu',
          multiLingualLabel: 'Bahasa Melayu - Malay',
          permission: MALAY,
          value: LOCALES.MS,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_336)
    ? [
        {
          label: 'Românã',
          multiLingualLabel: 'Românã - Romanian',
          permission: ROMANIAN,
          value: LOCALES.RO,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_337)
    ? [
        {
          label: 'Slovenčina',
          multiLingualLabel: 'Slovenčina - Slovak',
          permission: SLOVAK,
          value: LOCALES.SK,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_338)
    ? [
        {
          label: 'ภาษาไทย',
          multiLingualLabel: 'ภาษาไทย - Thai',
          permission: THAI,
          value: LOCALES.TH,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_339)
    ? [
        {
          label: 'Українська',
          multiLingualLabel: 'Українська - Ukrainian',
          permission: UKRAINIAN,
          value: LOCALES.UK,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_340)
    ? [
        {
          label: 'English (United Kingdom)',
          multiLingualLabel: 'English (United Kingdom)',
          permission: ENGLISH_UK,
          value: LOCALES.EN_GB,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_341)
    ? [
        {
          label: 'Svenska',
          multiLingualLabel: 'Svenska - Swedish',
          permission: SWEDISH,
          value: LOCALES.SV,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_342)
    ? [
        {
          label: 'Suomi',
          multiLingualLabel: 'Suomi - Finnish',
          permission: FINNISH,
          value: LOCALES.FI,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_343)
    ? [
        {
          label: 'Dansk',
          multiLingualLabel: 'Dansk - Danish',
          permission: DANISH,
          value: LOCALES.DA,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_344)
    ? [
        {
          label: 'Norsk nynorsk',
          multiLingualLabel: 'Norsk nynorsk - Norwegian',
          permission: NORWEGIAN,
          value: LOCALES.NO,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_345)
    ? [
        {
          label: 'Català',
          multiLingualLabel: 'Català - Catalan',
          permission: CATALAN,
          value: LOCALES.CA,
        },
      ]
    : []),
  ...(featureToggle(FEATURES.MAPI_346)
    ? [
        {
          label: 'Español (México)',
          multiLingualLabel: 'Español (México) - Spanish Mexico',
          permission: SPANISH_MX,
          value: LOCALES.ES_MX,
        },
      ]
    : []),
];

export const messageLocales = defineMessages({
  ar: {
    id: 'locale.ar',
    defaultMessage: 'Arabic',
  },
  bg: {
    id: 'locale.bg',
    defaultMessage: 'Bulgarian',
  },
  ca: {
    id: 'locale.ca',
    defaultMessage: 'Catalan',
  },
  cs: {
    id: 'locale.cs',
    defaultMessage: 'Czech',
  },
  da: {
    id: 'locale.da',
    defaultMessage: 'Danish',
  },
  de: {
    id: 'locale.de',
    defaultMessage: 'German',
  },
  el: {
    id: 'locale.el',
    defaultMessage: 'Greek',
  },
  en: {
    id: 'locale.en',
    defaultMessage: 'English',
  },
  'en-gb': {
    id: 'locale.en-gb',
    defaultMessage: 'English UK',
  },
  es: {
    id: 'locale.es',
    defaultMessage: 'Spanish',
  },
  'es-mx': {
    id: 'locale.es-mx',
    defaultMessage: 'Spanish Mexico',
  },
  fi: {
    id: 'locale.fi',
    defaultMessage: 'Finnish',
  },
  fr: {
    id: 'locale.fr',
    defaultMessage: 'French',
  },
  'fr-ca': {
    id: 'locale.fr-ca',
    defaultMessage: 'French Canada',
  },
  he: {
    id: 'locale.he',
    defaultMessage: 'Hebrew',
  },
  hi: {
    id: 'locale.hi',
    defaultMessage: 'Hindi',
  },
  hu: {
    id: 'locale.hu',
    defaultMessage: 'Hungarian',
  },
  it: {
    id: 'locale.it',
    defaultMessage: 'Italian',
  },
  ja: {
    id: 'locale.ja',
    defaultMessage: 'Japanese',
  },
  ko: {
    id: 'locale.ko',
    defaultMessage: 'Korean',
  },
  ms: {
    id: 'locale.ms',
    defaultMessage: 'Malay',
  },
  nl: {
    id: 'locale.nl',
    defaultMessage: 'Dutch',
  },
  no: {
    id: 'locale.no',
    defaultMessage: 'Norwegian',
  },
  pl: {
    id: 'locale.pl',
    defaultMessage: 'Polish',
  },
  pt: {
    id: 'locale.pt',
    defaultMessage: 'Portuguese',
  },
  'pt-br': {
    id: 'locale.pt-br',
    defaultMessage: 'Portuguese Brazil',
  },
  ro: {
    id: 'locale.ro',
    defaultMessage: 'Romanian',
  },
  ru: {
    id: 'locale.ru',
    defaultMessage: 'Russian',
  },
  sk: {
    id: 'locale.sk',
    defaultMessage: 'Slovak',
  },
  sv: {
    id: 'locale.sv',
    defaultMessage: 'Swedish',
  },
  th: {
    id: 'locale.th',
    defaultMessage: 'Thai',
  },
  tr: {
    id: 'locale.tr',
    defaultMessage: 'Turkish',
  },
  uk: {
    id: 'locale.uk',
    defaultMessage: 'Ukrainian',
  },
  vi: {
    id: 'locale.vi',
    defaultMessage: 'Vietnamese',
  },
  'zh-hans': {
    id: 'locale.zh_hans',
    defaultMessage: 'Chinese Simplified',
  },
  'zh-hant': {
    id: 'locale.zh_hant',
    defaultMessage: 'Chinese Traditional',
  },
});

export const TRANSLATED_LANGUAGES = localeOptions.map((option) => {
  return {
    ...option,
    translatedLabel: <FormattedMessage {...messageLocales[option.value.toLowerCase()]} />,
  };
});
